import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Layout, Menu, Avatar, Dropdown, Space, Breadcrumb, Badge, Button, theme,
  Popover, List, Typography, Divider,
} from 'antd'
import {
  MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined, LogoutOutlined,
  LockOutlined, DashboardOutlined, ShopOutlined, BellOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useAuthStore } from '@/store/auth'
import { notificationApi, pendingCountApi, type SystemNotification, type PendingCounts } from '@/api'
import type { MenuItem } from '@/types'

// 侧边栏菜单 key -> 待办计数字段的映射。菜单 key 变更时必须同步改这里，
// 别把映射散落到 buildMenuItems 的调用逻辑里。
// 纠纷升级仲裁（dispute_arbitration）故意不在此列：后台目前没有可以处理
// 仲裁的页面/路由，角标挂不上去，见 ISSUES.md 对应条目。
const PENDING_BADGE_MAP: Record<string, keyof PendingCounts> = {
  '/distribution/withdrawal': 'withdrawal',
  '/ad/campaign-review': 'ad_campaign',
  '/ad': 'ad_slot_order',
}

// 返回菜单项数组，以及本级（含所有子级）里是否存在待办计数 > 0，供父级菜单显示小红点
function buildMenuItemsWithPending(menus: MenuItem[], pendingCounts?: PendingCounts): [MenuProps['items'], boolean] {
  let anyPending = false
  const items = menus
    .filter(item => item.type !== 3)
    .map(item => {
      const [children, childPending] = item.children?.length
        ? buildMenuItemsWithPending(item.children, pendingCounts)
        : [undefined, false]
      const countKey = PENDING_BADGE_MAP[item.path]
      const count = countKey && pendingCounts ? pendingCounts[countKey] : 0
      const hasChildren = !!children?.length
      const showCount = !hasChildren && count > 0
      const showDot = hasChildren && childPending
      if (showCount || showDot) anyPending = true
      return {
        key: item.path,
        label: showCount
          ? <Badge count={count} size="small" overflowCount={99} offset={[10, 2]}>
              <span style={{ color: '#fff' }}>{item.name}</span>
            </Badge>
          : showDot
            ? <Badge dot offset={[6, 6]}><span style={{ color: '#fff' }}>{item.name}</span></Badge>
            : item.name,
        ...(hasChildren ? { children } : {}),
      }
    })
  return [items, anyPending]
}

function buildMenuItems(menus: MenuItem[], pendingCounts?: PendingCounts): MenuProps['items'] {
  return buildMenuItemsWithPending(menus, pendingCounts)[0]
}

const { Sider, Content, Header } = Layout
const { Text } = Typography

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, loaded, fetchUserInfo, menus, isPathAllowed } = useAuthStore()
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken()

  // 通知状态
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifyList, setNotifyList] = useState<SystemNotification[]>([])
  const [notifyOpen, setNotifyOpen] = useState(false)
  // 侧边栏待办角标（与上面的通知铃铛数据源不同，见 PENDING_BADGE_MAP 注释）
  const [pendingCounts, setPendingCounts] = useState<PendingCounts>()

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationApi.unreadCount()
      setUnreadCount(res?.count ?? 0)
    } catch { /* 静默失败 */ }
    try {
      const counts = await pendingCountApi.get()
      setPendingCounts(counts)
    } catch { /* 静默失败 */ }
  }, [])

  const fetchNotifyList = useCallback(async () => {
    try {
      const res = await notificationApi.list(1, 10)
      setNotifyList(res?.list ?? [])
    } catch { /* 静默失败 */ }
  }, [])

  const handleReadAll = useCallback(async () => {
    await notificationApi.readAll()
    setUnreadCount(0)
    setNotifyList(prev => prev.map(n => ({ ...n, is_read: true })))
  }, [])

  useEffect(() => {
    if (!loaded) fetchUserInfo()
  }, [loaded, fetchUserInfo])

  // 登录后立即拉一次未读数，之后每 60 秒轮询
  useEffect(() => {
    fetchUnreadCount()
    const timer = setInterval(fetchUnreadCount, 60_000)
    return () => clearInterval(timer)
  }, [fetchUnreadCount])

  const onBellClick = useCallback((open: boolean) => {
    setNotifyOpen(open)
    if (open) fetchNotifyList()
  }, [fetchNotifyList])

  // 侧边栏菜单：工作台/商家端首页固定展示，其余从当前登录用户的权限菜单树动态生成
  const menuItems: MenuProps['items'] = useMemo(() => [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台' },
    { key: '/merchant-dashboard', icon: <ShopOutlined />, label: '商家端首页' },
    ...(buildMenuItems(menus, pendingCounts) ?? []),
  ], [menus, pendingCounts])

  // 路由级权限拦截：无权限的页面禁止直接通过 URL 访问
  useEffect(() => {
    if (!loaded) return
    if (!isPathAllowed(location.pathname)) {
      navigate('/dashboard', { replace: true })
    }
  }, [loaded, location.pathname, isPathAllowed, navigate])

  // 收集所有父节点 key（有 children），点击时只展开不导航
  const parentKeys = useMemo(() => {
    const keys = new Set<string>()
    const collect = (items: MenuProps['items']) => {
      items?.forEach(item => {
        if (item && 'children' in item && item.children) {
          keys.add(item.key as string)
          collect(item.children as MenuProps['items'])
        }
      })
    }
    collect(menuItems)
    return keys
  }, [menuItems])

  // 受控展开 keys：路由变化时自动展开当前路径的父节点，用户也可手动折叠/展开
  const calcOpenKeys = (pathname: string) => {
    const result: string[] = []
    const findParents = (items: MenuProps['items'], target: string): boolean => {
      for (const item of items ?? []) {
        if (!item) continue
        if ('children' in item && item.children) {
          if (findParents(item.children as MenuProps['items'], target)) {
            result.push(item.key as string)
            return true
          }
        } else if (item.key === target) {
          return true
        }
      }
      return false
    }
    findParents(menuItems, pathname)
    return result
  }
  const [openKeys, setOpenKeys] = useState<string[]>(() => calcOpenKeys(location.pathname))
  useEffect(() => {
    setOpenKeys(prev => {
      const parents = calcOpenKeys(location.pathname)
      const merged = Array.from(new Set([...prev, ...parents]))
      return merged
    })
  }, [location.pathname])

  // 面包屑
  const breadcrumbItems = useMemo(() => {
    const paths = location.pathname.split('/').filter(Boolean)
    const items = [{ title: '首页' }]
    const nameMap: Record<string, string> = {
      system: '系统管理', user: '用户管理', role: '角色管理', menu: '菜单管理',
      log: '操作日志', product: '商品管理', spu: '商品列表', category: '分类管理',
      brand: '品牌管理', attr: '属性管理', merchant: '商家管理', logistics: '物流管理',
      freight: '运费模板', company: '物流公司', payment: '支付管理', i18n: '多语言',
      dashboard: '工作台', create: '新建', edit: '编辑', order: '订单管理',
      'line-key': '线路Key管理', 'merchant-notify': '单发通知', 'merchant-dashboard': '商家端首页',
      'fund-adjustments': '资金调整记录', bill: '账单查询', points: '积分查询',
      'activation-center': '激活码', 'reward-code': '团队补贴码',
    }
    paths.forEach(p => items.push({ title: nameMap[p] || p }))
    return items
  }, [location.pathname])

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    { key: 'password', icon: <LockOutlined />, label: '修改密码' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ]

  const onUserMenu: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') logout()
    if (key === 'password') navigate('/system/user')
  }

  // 通知弹窗内容
  const notifyContent = (
    <div style={{ width: 320 }}>
      <List
        size="small"
        dataSource={notifyList}
        locale={{ emptyText: '暂无通知' }}
        renderItem={item => (
          <List.Item style={{ padding: '8px 0', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              {!item.is_read && (
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', background: '#1677ff',
                  flexShrink: 0, marginTop: 6,
                }} />
              )}
              <div style={{ flex: 1, paddingLeft: item.is_read ? 14 : 0 }}>
                <div style={{ fontWeight: item.is_read ? 400 : 600, fontSize: 13 }}>
                  {item.title}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>{item.content}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {item.created_at?.slice(0, 16)}
                  </Text>
                </div>
              </div>
            </div>
          </List.Item>
        )}
      />
      {notifyList.length > 0 && (
        <>
          <Divider style={{ margin: '4px 0' }} />
          <div style={{ textAlign: 'center' }}>
            <Button type="link" size="small" onClick={handleReadAll}>
              全部已读
            </Button>
          </div>
        </>
      )}
    </div>
  )

  return (
    <Layout className="admin-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        theme="dark"
        style={{ overflow: 'auto', height: '100vh', position: 'fixed', left: 0, top: 0, bottom: 0 }}
      >
        <div style={{
          height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: collapsed ? 16 : 18, fontWeight: 600, letterSpacing: 1,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          {collapsed ? '🛒' : '🛒 Mall Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          onClick={({ key }) => { if (!parentKeys.has(key)) navigate(key) }}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'margin-left 0.2s' }}>
        <Header className="admin-header" style={{ background: colorBgContainer }}>
          <Space>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
            <Breadcrumb items={breadcrumbItems} />
          </Space>
          <Space size={16}>
            <Popover
              content={notifyContent}
              title="系统通知"
              trigger="click"
              open={notifyOpen}
              onOpenChange={onBellClick}
              placement="bottomRight"
              overlayStyle={{ maxHeight: 420, overflow: 'auto' }}
            >
              <Badge count={unreadCount} size="small" overflowCount={99}>
                <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
              </Badge>
            </Popover>
            <Dropdown menu={{ items: userMenuItems, onClick: onUserMenu }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size="small" icon={<UserOutlined />} src={user?.avatar} />
                <span style={{ fontSize: 14 }}>{user?.nickname || user?.username || 'Admin'}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ margin: 16, background: colorBgContainer, borderRadius: borderRadiusLG, padding: 0, minHeight: 'calc(100vh - 88px)' }}>
          <div className="page-container">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminLayout
